import React from "react"
import { LuArrowDownToLine, LuArrowUpToLine, LuFoldVertical } from "react-icons/lu"
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md"
import { MobergColumn, MobergIconSize, MobergNumberInput, MobergRow, MobergText } from "../../../../../Moberg"
import { GoalDirection } from "../../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails"

// The pieces a goal is set with: which way the goal goes, then its values in
// that shape ("below 22 mmHg", "60 to 110 bpm", "above 92 %").

const DIRECTIONS: { label: string, icon: React.ReactNode, value: GoalDirection }[] = [
	{ label: "Below", icon: <LuArrowDownToLine size={14} />, value: "below" },
	{ label: "Between", icon: <LuFoldVertical size={14} />, value: "between" },
	{ label: "Above", icon: <LuArrowUpToLine size={14} />, value: "above" },
]

export const DirectionPicker: React.FC<{ value: GoalDirection, onChange: (direction: GoalDirection) => void }> = ({ value, onChange }) => (
	<div role="radiogroup" aria-label="In goal when" style={{ display: "inline-flex", gap: "2px", padding: "3px", borderRadius: "8px", border: "1px solid var(--color-border-light)", background: "var(--color-surface-primary)", flexShrink: 0 }}>
		{DIRECTIONS.map(direction => {
			const selected = direction.value === value
			return (
				<button
					key={direction.value}
					type="button"
					role="radio"
					aria-checked={selected}
					onClick={() => onChange(direction.value)}
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: "6px",
						padding: "6px 12px",
						borderRadius: "6px",
						border: `1px solid ${selected ? "var(--color-accent-interactive)" : "transparent"}`,
						background: selected ? "color-mix(in srgb, var(--color-accent-interactive) 12%, var(--color-surface-panel))" : "transparent",
						color: selected ? "var(--color-accent-interactive)" : "var(--text-secondary-color)",
						font: "inherit",
						fontWeight: selected ? 600 : 500,
						cursor: "pointer",
						whiteSpace: "nowrap",
						transition: "background-color 120ms, color 120ms",
					}}
				>
					{direction.icon}{direction.label}
				</button>
			)
		})}
	</div>
)

/* The values a direction uses, and only those. Keyed by direction only, so a box keeps its
   focus while a value is typed. */
export const GoalValues: React.FC<{ id: string, direction: GoalDirection, lo: number | null, hi: number | null, units: string, onChange: (changes: { lo?: number | null, hi?: number | null }) => void }> = ({ id, direction, lo, hi, units, onChange }) => (
	<div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
		{direction !== "below" && (
			<MobergNumberInput key={`${id}-lo-${direction}`} placeholder="low" value={lo} onChange={value => onChange({ lo: value })} style={{ width: "76px" }} />
		)}
		{direction === "between" && <Hint>to</Hint>}
		{direction !== "above" && (
			<MobergNumberInput key={`${id}-hi-${direction}`} placeholder="high" value={hi} onChange={value => onChange({ hi: value })} style={{ width: "76px" }} />
		)}
		{units && <Hint>{units}</Hint>}
	</div>
)

/* The goal's on/off switch, with the state written beside it. Off is an outlined track with a
   grey knob, on a filled accent track, so both states read in light and dark. */
export const GoalSwitch: React.FC<{ label: string, checked: boolean, onChange: (checked: boolean) => void }> = ({ label, checked, onChange }) => (
	<MobergRow gap="8px" expand={false}>
		<MobergText fontWeight="600" style={{ color: checked ? "var(--color-accent-interactive)" : "var(--text-secondary-color)" }}>{checked ? "On" : "Off"}</MobergText>
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			aria-label={label}
			onClick={() => onChange(!checked)}
			style={{
				position: "relative",
				width: "44px",
				height: "24px",
				padding: 0,
				borderRadius: "12px",
				border: `2px solid ${checked ? "var(--color-accent-interactive)" : "var(--text-secondary-color)"}`,
				background: checked ? "var(--color-accent-interactive)" : "var(--color-surface-panel)",
				cursor: "pointer",
				transition: "background-color 120ms, border-color 120ms",
			}}
		>
			<span style={{ position: "absolute", top: "3px", left: checked ? "23px" : "3px", width: "14px", height: "14px", borderRadius: "50%", background: checked ? "var(--color-surface-panel)" : "var(--text-secondary-color)", transition: "left 120ms, background-color 120ms" }} />
		</button>
	</MobergRow>
)

/* One way of showing a goal, as a card: a small picture of what it draws, its name, a line
   saying what it draws, and any setting that belongs to it. The whole card switches it. */
export const GoalOptionCard: React.FC<{ label: string, description: string, picture: React.ReactNode, checked: boolean, onChange: (checked: boolean) => void, children?: React.ReactNode }> = ({ label, description, picture, checked, onChange, children }) => (
	<div
		role="checkbox"
		aria-checked={checked}
		aria-label={label}
		tabIndex={0}
		onClick={() => onChange(!checked)}
		onKeyDown={event => {
			if (event.key === " " || event.key === "Enter") {
				event.preventDefault()
				onChange(!checked)
			}
		}}
		style={{
			display: "flex",
			flexDirection: "column",
			gap: "10px",
			padding: "12px",
			borderRadius: "8px",
			cursor: "pointer",
			userSelect: "none",
			border: `1px solid ${checked ? "var(--color-accent-interactive)" : "var(--color-border-default)"}`,
			boxShadow: checked ? "inset 0 0 0 1px var(--color-accent-interactive)" : "none",
			background: checked ? "color-mix(in srgb, var(--color-accent-interactive) 8%, var(--color-surface-panel))" : "var(--color-surface-panel)",
			transition: "border-color 120ms, background-color 120ms",
		}}
	>
		{picture}
		<MobergColumn gap="2px">
			<MobergRow horizontalAlign="space-between" gap="8px">
				<MobergText fontWeight="600">{label}</MobergText>
				{checked
					? <MdCheckBox size={MobergIconSize.REGULAR} color="var(--color-accent-interactive)" aria-hidden="true" />
					: <MdCheckBoxOutlineBlank size={MobergIconSize.REGULAR} color="var(--text-secondary-color)" aria-hidden="true" />
				}
			</MobergRow>
			<Hint style={{ fontSize: "13px" }}>{description}</Hint>
		</MobergColumn>
		{children && <div onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>{children}</div>}
	</div>
)

// Pictures for the cards: a few values against a goal band drawn as the graph draws it.
const PICTURE_STYLE: React.CSSProperties = { display: "block", width: "100%", height: "44px", borderRadius: "6px", background: "var(--color-surface-primary)", border: "1px solid var(--color-border-light)" }
const VALUES = "2,18 12,14 20,2 28,15 38,20 46,30 56,17 70,14"
const OUT_OF_GOAL_RED = "#E54E58"

const Picture: React.FC = ({ children }) => (
	<svg viewBox="0 0 72 32" preserveAspectRatio="none" style={PICTURE_STYLE} aria-hidden="true">{children}</svg>
)

const GoalBand = () => (
	<>
		<line x1="0" x2="72" y1="9" y2="9" stroke="var(--text-secondary-color)" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
		<line x1="0" x2="72" y1="23" y2="23" stroke="var(--text-secondary-color)" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
	</>
)

const Values = () => <polyline points={VALUES} fill="none" stroke="var(--color-accent-interactive)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />

export const GoalLinesPicture = () => <Picture><GoalBand /><Values /></Picture>

export const ShadingPicture = () => (
	<Picture>
		<polygon points="15.3,9 20,2 24.3,9" fill={OUT_OF_GOAL_RED} opacity="0.6" />
		<polygon points="40.4,23 46,30 51.4,23" fill={OUT_OF_GOAL_RED} opacity="0.6" />
		<GoalBand />
		<Values />
	</Picture>
)

const BAR_BLOCKS = ["#4CAF50", "#4CAF50", "#FF9800", "#4CAF50", "#4CAF50", OUT_OF_GOAL_RED, "#4CAF50", "#4CAF50"]

export const TimeInGoalBarPicture = () => (
	<Picture>
		{BAR_BLOCKS.map((color, index) => <rect key={index} x={4 + index * 8} y="10" width="7.5" height="12" fill={color} />)}
	</Picture>
)

/* A short line that says what a setting does. */
export const Hint: React.FC<{ children: React.ReactNode, style?: React.CSSProperties }> = ({ children, style }) => (
	<MobergText style={{ color: "var(--text-secondary-color)", fontSize: "14px", ...style }}>{children}</MobergText>
)
